import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';

import type { FeatureProps } from '@/components/KeyFeatures/components/Features/Feature';
import type { WatchAttributesFragment } from '@/graphql/saleor/api';
import { getDropdownValue, getNumericValue } from '@/lib/attributes';
import { pdpMessages } from '@/messages/product';

import caseMaterialSvg from '@/assets/caseMaterial.svg';
import diameterSvg from '@/assets/diameter.svg';
import powerReserveSvg from '@/assets/powerReserve.svg';
import thicknessSvg from '@/assets/thickness.svg';
import waterResistanceSvg from '@/assets/waterResistance.svg';

export const getKeyFeatures = (
  attributes: WatchAttributesFragment,
  formatMessage: (message: MessageDescriptor) => string,
): FeatureProps[] => {
  const thickness = getNumericValue(attributes.thickness);
  const caseMaterial =
    attributes.caseMaterial?.values[0]?.translation?.plainText;
  const isThicknessPositive = thickness?.value
    ? parseFloat(thickness?.value) > 0
    : false;
  const waterResistance = getDropdownValue(attributes.waterResistance);
  const diameter = getNumericValue(attributes.diameter);
  const powerReserve = getDropdownValue(attributes.powerReserve);
  return [
    ...(caseMaterial
      ? [
          {
            name: formatMessage(pdpMessages.caseMaterial),
            value: caseMaterial,
            icon: {
              alt: 'Case material',
              src: caseMaterialSvg,
            },
          },
        ]
      : []),
    ...(waterResistance
      ? [
          {
            name: formatMessage(pdpMessages.keyFeaturesWaterResistance),
            value: waterResistance,
            icon: {
              alt: 'Water resistance',
              src: waterResistanceSvg,
            },
          },
        ]
      : []),
    ...(diameter?.value
      ? [
          {
            name: formatMessage(pdpMessages.keyFeaturesDiameter),
            value: `${Number(diameter.value)} mm`,
            icon: {
              alt: 'Diameter',
              src: diameterSvg,
            },
          },
        ]
      : []),
    ...(thickness?.value && isThicknessPositive
      ? [
          {
            name: formatMessage(pdpMessages.keyFeaturesWaterThickness),
            value: `${Number(thickness.value)} mm`,
            icon: {
              alt: 'Thickness',
              src: thicknessSvg,
            },
          },
        ]
      : []),
    ...(powerReserve
      ? [
          {
            name: formatMessage(pdpMessages.keyFeaturesPowerReserve),
            value: getDropdownValue(attributes.powerReserve),
            icon: {
              alt: 'Power reserve',
              src: powerReserveSvg,
            },
          },
        ]
      : []),
  ];
};

export const useKeyFeatures = (
  attributes: WatchAttributesFragment,
): FeatureProps[] => {
  const { formatMessage } = useIntl();
  return getKeyFeatures(attributes, formatMessage);
};
